import { COURSES } from './courses';

export interface CourseMeta {
    id: string;
    title: string;
    icon: string;
    tagline: string;
    prerequisites: string[];
    phase: number;
    isAvailable: boolean;
}

export interface PhaseDefinition {
    number: number;
    name: string;
    tagline: string;
    /** Hex color for inline styles (progress bars, glows) */
    color: string;
    colorClass: string;
    borderClass: string;
    bgClass: string;
    courses: CourseMeta[];
}

export const PHASES: PhaseDefinition[] = [
    // ── Phase 0 — Your Toolkit ───────────────────────────────────────────────
    {
        number: 0,
        name: 'Your Toolkit',
        tagline: 'Master your weapons before writing a line of code.',
        color: '#7c3aed',
        colorClass: 'text-game-primary-l',
        borderClass: 'border-game-primary/40',
        bgClass: 'bg-game-primary/10',
        courses: [
            {
                id: 'terminal',
                title: 'Terminal — Your Command Center',
                icon: '⌨️',
                tagline: 'The keyboard is your controller. The terminal is your HUD.',
                prerequisites: [],
                phase: 0,
                isAvailable: false,
            },
            {
                id: 'git',
                title: 'GIT — The Save System',
                icon: '💾',
                tagline: 'Never lose progress. Branch your reality. Ship with confidence.',
                prerequisites: ['terminal'],
                phase: 0,
                isAvailable: true,
            },
        ],
    },

    // ── Phase 1 — How the Web Works ──────────────────────────────────────────
    {
        number: 1,
        name: 'How the Web Works',
        tagline: 'Understand the invisible pipes before you pour anything into them.',
        color: '#38bdf8',
        colorClass: 'text-sky-400',
        borderClass: 'border-sky-400/40',
        bgClass: 'bg-sky-400/10',
        courses: [
            {
                id: 'networking-basics',
                title: 'Networking — How Computers Find Each Other',
                icon: '🗺️',
                tagline: 'IP, TCP/UDP, ports — the wiring behind every request.',
                prerequisites: ['terminal'],
                phase: 1,
                isAvailable: true,
            },
            {
                id: 'http',
                title: 'HTTP — How the Internet Talks',
                icon: '🌐',
                tagline: 'Every line of code you write eventually sends a message. Learn to speak it.',
                prerequisites: ['networking-basics'],
                phase: 1,
                isAvailable: true,
            },
            {
                id: 'dns',
                title: "DNS — The Internet's Phone Book",
                icon: '📒',
                tagline: 'How google.com becomes 142.250.185.46.',
                prerequisites: ['networking-basics'],
                phase: 1,
                isAvailable: true,
            },
            {
                id: 'browsers',
                title: 'Browsers — What Happens When You Hit Enter',
                icon: '🖥️',
                tagline: 'DNS → TCP → HTTP → render. Know the full chain.',
                prerequisites: ['http', 'dns'],
                phase: 1,
                isAvailable: true,
            },
        ],
    },

    // ── Phase 2 — Build the Frontend ─────────────────────────────────────────
    {
        number: 2,
        name: 'Build the Frontend',
        tagline: 'Create the interfaces players see and touch.',
        color: '#10b981',
        colorClass: 'text-game-accent',
        borderClass: 'border-game-accent/40',
        bgClass: 'bg-game-accent/10',
        courses: [
            {
                id: 'html-css',
                title: 'HTML & CSS — Building the Web',
                icon: '🏗️',
                tagline: 'Structure and style. The skeleton and skin of every web page.',
                prerequisites: ['terminal'],
                phase: 2,
                isAvailable: true,
            },
            {
                id: 'javascript',
                title: 'JavaScript — Making Pages Interactive',
                icon: '⚙️',
                tagline: 'Add logic, interactivity, and power. Includes OOP: classes, this, inheritance.',
                prerequisites: ['html-css'],
                phase: 2,
                isAvailable: true,
            },
            {
                id: 'typescript',
                title: 'TypeScript — JavaScript Grown Up',
                icon: '🔷',
                tagline: 'Types, interfaces, generics. Catch bugs before they ship.',
                prerequisites: ['javascript'],
                phase: 2,
                isAvailable: false,
            },
        ],
    },

    // ── Phase 3 — Go Full Stack ───────────────────────────────────────────────
    {
        number: 3,
        name: 'Go Full Stack',
        tagline: 'Connect the frontend to the real world.',
        color: '#f59e0b',
        colorClass: 'text-amber-400',
        borderClass: 'border-amber-400/40',
        bgClass: 'bg-amber-400/10',
        courses: [
            {
                id: 'react',
                title: 'React — Building UIs That React',
                icon: '🌀',
                tagline: 'Components, state, props. Build UIs that think.',
                prerequisites: ['javascript'],
                phase: 3,
                isAvailable: false,
            },
            {
                id: 'nodejs',
                title: 'Node.js — JavaScript on the Server',
                icon: '⚙️',
                tagline: 'One language, everywhere. Run JavaScript outside the browser.',
                prerequisites: ['javascript', 'terminal'],
                phase: 3,
                isAvailable: false,
            },
            {
                id: 'databases',
                title: 'Databases — How Data is Stored',
                icon: '🗄️',
                tagline: 'Tables, relationships, ACID. What a database actually is before you query one.',
                prerequisites: ['terminal'],
                phase: 3,
                isAvailable: false,
            },
            {
                id: 'sql',
                title: 'SQL — Talk to Your Database',
                icon: '🔍',
                tagline: 'Ask any question of your data and get an instant answer.',
                prerequisites: ['databases'],
                phase: 3,
                isAvailable: false,
            },
            {
                id: 'rest-apis',
                title: 'REST APIs — How Apps Talk to Each Other',
                icon: '🔗',
                tagline: 'Design, build, and consume APIs. Make services talk.',
                prerequisites: ['http', 'javascript'],
                phase: 3,
                isAvailable: false,
            },
        ],
    },

    // ── Phase 4 — Production ─────────────────────────────────────────────────
    {
        number: 4,
        name: 'Production Ready',
        tagline: "Ship software that doesn't break in the real world.",
        color: '#ef4444',
        colorClass: 'text-game-danger',
        borderClass: 'border-game-danger/40',
        bgClass: 'bg-game-danger/10',
        courses: [
            {
                id: 'web-security',
                title: "Web Security — Don't Ship Vulnerabilities",
                icon: '🛡️',
                tagline: 'XSS, CSRF, SQL injection. Know the attacks before you build the defences.',
                prerequisites: ['http', 'javascript'],
                phase: 4,
                isAvailable: false,
            },
            {
                id: 'auth-security',
                title: 'Auth & Security — Passwords, Tokens, OAuth',
                icon: '🔐',
                tagline: 'JWTs, OAuth 2.0, sessions. Keep users and data safe.',
                prerequisites: ['rest-apis', 'web-security'],
                phase: 4,
                isAvailable: false,
            },
            {
                id: 'docker',
                title: 'Docker — Ship Anywhere',
                icon: '🐳',
                tagline: 'If it runs locally, it runs everywhere. Package once, deploy anywhere.',
                prerequisites: ['terminal', 'nodejs'],
                phase: 4,
                isAvailable: false,
            },
            {
                id: 'github-actions',
                title: 'GitHub Actions — Automate Everything',
                icon: '⚡',
                tagline: 'Test, build, and deploy without touching a button.',
                prerequisites: ['git', 'docker'],
                phase: 4,
                isAvailable: false,
            },
            {
                id: 'nextjs',
                title: 'Next.js — React + Backend in One',
                icon: '▲',
                tagline: 'SSR, SSG, API routes. The full-stack React framework.',
                prerequisites: ['react', 'nodejs'],
                phase: 4,
                isAvailable: false,
            },
        ],
    },

    // ── Phase 5 — The AI Age ─────────────────────────────────────────────────
    {
        number: 5,
        name: 'The AI Age',
        tagline: 'Build the tools reshaping what software even is.',
        color: '#facc15',
        colorClass: 'text-yellow-400',
        borderClass: 'border-yellow-400/40',
        bgClass: 'bg-yellow-400/10',
        courses: [
            {
                id: 'python',
                title: 'Python — Language of AI',
                icon: '🐍',
                tagline: 'Clean syntax, massive ecosystem. The lingua franca of machine learning.',
                prerequisites: ['terminal'],
                phase: 5,
                isAvailable: false,
            },
            {
                id: 'ml-basics',
                title: "ML Basics — What's Under the Hood",
                icon: '🧠',
                tagline: 'Models, training, embeddings, vectors. Understand AI before you wield it.',
                prerequisites: ['python'],
                phase: 5,
                isAvailable: false,
            },
            {
                id: 'prompt-engineering',
                title: 'Prompt Engineering — Talk to AI Like a Dev',
                icon: '🧙',
                tagline: 'The difference between "meh" and "magic" is how you ask.',
                prerequisites: ['rest-apis'],
                phase: 5,
                isAvailable: false,
            },
            {
                id: 'ai-apis',
                title: 'AI APIs — Build with LLMs',
                icon: '🤖',
                tagline: 'Call GPT-4, Claude, Gemini. Add AI to anything you build.',
                prerequisites: ['rest-apis', 'prompt-engineering'],
                phase: 5,
                isAvailable: false,
            },
            {
                id: 'rag',
                title: 'RAG — Give AI a Memory',
                icon: '📚',
                tagline: 'Retrieval-augmented generation. Ground AI responses in your own data.',
                prerequisites: ['ai-apis', 'sql'],
                phase: 5,
                isAvailable: false,
            },
            {
                id: 'agents',
                title: 'Agents — Build Autonomous AI Workflows',
                icon: '🦾',
                tagline: 'Systems that plan, act, and adapt. Beyond single-shot prompts.',
                prerequisites: ['rag', 'ai-apis'],
                phase: 5,
                isAvailable: false,
            },
        ],
    },
];

// ── Helper functions ────────────────────────────────────────────────────────

export type ProgressMap = Record<string, 'completed' | 'in_progress' | 'locked'>;

const ALL_COURSES = PHASES.flatMap(p => p.courses);

/** A course is completed when every one of its modules is 'completed'. */
export function isCourseCompleted(courseId: string, progress: ProgressMap): boolean {
    const course = COURSES[courseId];
    if (!course) return false;
    return course.modules.every((m: any) => progress[m.slug] === 'completed');
}

/** A course is unlocked when all its prerequisites are completed. */
export function isCourseUnlocked(courseId: string, progress: ProgressMap): boolean {
    const meta = ALL_COURSES.find(c => c.id === courseId);
    if (!meta) return false;
    if (meta.prerequisites.length === 0) return true;
    return meta.prerequisites.every(prereqId => isCourseCompleted(prereqId, progress));
}

/** Returns 0–100 progress for a phase (based on available courses only). */
export function getPhaseProgress(phaseNum: number, progress: ProgressMap): number {
    const phase = PHASES[phaseNum];
    if (!phase) return 0;
    const available = phase.courses.filter(c => c.isAvailable);
    if (available.length === 0) return 0;
    const completed = available.filter(c => isCourseCompleted(c.id, progress)).length;
    return Math.round((completed / available.length) * 100);
}

/**
 * Phase 0 is always unlocked.
 * Phase N unlocks when >= 50% of the previous phase's courses are completed.
 * For Phase 0 (2 courses): need 1. For Phase 1 (4 courses): need 2. Etc.
 */
export function isPhaseUnlocked(phaseNum: number, progress: ProgressMap): boolean {
    if (phaseNum === 0) return true;
    const prevPhase = PHASES[phaseNum - 1];
    if (!prevPhase) return false;
    const total = prevPhase.courses.length;
    const completed = prevPhase.courses.filter(c => isCourseCompleted(c.id, progress)).length;
    return completed / total >= 0.5;
}

/** Total XP earnable in a phase (available courses only). */
export function getPhaseXP(phaseNum: number): number {
    const phase = PHASES[phaseNum];
    if (!phase) return 0;
    return phase.courses.reduce((sum, course) => {
        const c = COURSES[course.id];
        return sum + (c?.xp_reward ?? 0);
    }, 0);
}

/** True if the user has started any module at all. */
export function hasAnyProgress(progress: ProgressMap): boolean {
    return Object.keys(progress).length > 0;
}

/**
 * Returns the next recommended course:
 * first unlocked, incomplete, available course in phase order.
 */
export function getRecommendedNext(progress: ProgressMap): CourseMeta | null {
    for (const phase of PHASES) {
        for (const course of phase.courses) {
            if (
                course.isAvailable &&
                !isCourseCompleted(course.id, progress) &&
                isCourseUnlocked(course.id, progress)
            ) {
                return course;
            }
        }
    }
    return null;
}
